import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../config/apiBase";
import {
  SEARCH_RADIUS_KM,
  buildGoogleMapsEmbedUrl,
  buildOpenInMapsUrl,
  buildOsmEmbedUrl,
  geocodePlaceName,
  getBlockingBookings,
  haversineDistanceKm,
  parseVehicleCoordinates,
} from "../utils/vehicleLocationUtils";

const formatBookingDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatDistance = (km) => {
  if (km == null || Number.isNaN(km)) return "—";
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${Math.round(km)} km`;
};

const VehicleLocationInsight = ({ vehicleId, vehicleLocation, sellerCity }) => {
  const theme = useTheme();
  const coords = useMemo(
    () => parseVehicleCoordinates(vehicleLocation),
    [vehicleLocation]
  );

  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [bookingsError, setBookingsError] = useState(null);
  const [sellerGeo, setSellerGeo] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState(null);

  const mapEmbedUrl = useMemo(
    () => (coords ? buildGoogleMapsEmbedUrl(coords.lat, coords.lng) : null),
    [coords]
  );
  const osmEmbedUrl = useMemo(
    () => (coords ? buildOsmEmbedUrl(coords.lat, coords.lng) : null),
    [coords]
  );
  const [embedSrc, setEmbedSrc] = useState(null);

  useEffect(() => {
    setEmbedSrc(mapEmbedUrl);
  }, [mapEmbedUrl]);

  useEffect(() => {
    if (!vehicleId) return undefined;

    let cancelled = false;

    const fetchBookings = async () => {
      setBookingsLoading(true);
      setBookingsError(null);

      try {
        const response = await axios.get(
          `${API_BASE_URL}/bookings/getAllBookingsByVehicleId/${vehicleId}`
        );
        if (cancelled) return;

        if (response.data?.status && Array.isArray(response.data.data)) {
          setBookings(response.data.data);
        } else {
          setBookings([]);
        }
      } catch (err) {
        if (!cancelled) {
          setBookingsError(err.message || "Failed to load bookings");
        }
      } finally {
        if (!cancelled) setBookingsLoading(false);
      }
    };

    fetchBookings();
    return () => {
      cancelled = true;
    };
  }, [vehicleId]);

  useEffect(() => {
    const city = String(sellerCity || "").trim();
    if (!city || city.toLowerCase() === "location not specified") {
      setSellerGeo(null);
      setGeoError(null);
      return undefined;
    }

    let cancelled = false;

    const resolveCity = async () => {
      setGeoLoading(true);
      setGeoError(null);

      try {
        const result = await geocodePlaceName(city);
        if (cancelled) return;
        setSellerGeo(result);
        if (!result) setGeoError(`Could not geocode seller city "${city}"`);
      } catch (err) {
        if (!cancelled) {
          setGeoError(err.message || "Failed to geocode seller city");
        }
      } finally {
        if (!cancelled) setGeoLoading(false);
      }
    };

    resolveCity();
    return () => {
      cancelled = true;
    };
  }, [sellerCity]);

  const distanceFromSellerKm = useMemo(() => {
    if (!coords || !sellerGeo) return null;
    return haversineDistanceKm(
      coords.lat,
      coords.lng,
      sellerGeo.lat,
      sellerGeo.lng
    );
  }, [coords, sellerGeo]);

  const blockingBookings = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const horizon = new Date(today);
    horizon.setMonth(horizon.getMonth() + 6);

    return getBlockingBookings(bookings, today, horizon).sort(
      (a, b) => new Date(a.StartDate) - new Date(b.StartDate)
    );
  }, [bookings]);

  const distanceSeverity =
    distanceFromSellerKm == null
      ? null
      : distanceFromSellerKm > SEARCH_RADIUS_KM
        ? "error"
        : distanceFromSellerKm > 50
          ? "warning"
          : "success";

  return (
    <Box sx={{ width: "100%", mb: 4 }}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          borderLeft: `4px solid ${theme.palette.primary.main}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
          <LocationOnIcon color="primary" />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Vehicle location & search visibility
          </Typography>
        </Box>

        {!coords ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            No valid GPS pin on this vehicle
            {vehicleLocation ? ` (“${vehicleLocation}” could not be parsed)` : ""}.
            Customer search requires coordinates in <strong>lat,lng</strong> format.
          </Alert>
        ) : (
          <>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                minHeight: { xs: 200, sm: 240 },
                borderRadius: "20px",
                overflow: "hidden",
                border: "1px solid #e2e8f0",
                boxShadow:
                  "0 1px 0 rgba(255, 255, 255, 0.7) inset, 0 18px 42px -28px rgba(15, 23, 42, 0.35)",
                mb: 2,
                bgcolor: "#eef2f7",
              }}
            >
              {embedSrc ? (
                <iframe
                  title="Vehicle location on map"
                  src={embedSrc}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  onError={() => {
                    if (osmEmbedUrl && embedSrc !== osmEmbedUrl) {
                      setEmbedSrc(osmEmbedUrl);
                    }
                  }}
                  style={{
                    display: "block",
                    width: "100%",
                    height: 240,
                    border: 0,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: { xs: 200, sm: 240 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
                mb: 2,
              }}
            >
              <Chip
                icon={<LocationOnIcon />}
                label={`${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`}
                color="primary"
                variant="outlined"
              />
              <Link
                href={buildOpenInMapsUrl(coords.lat, coords.lng)}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ fontSize: 14 }}
              >
                Open in Google Maps
              </Link>
            </Box>
          </>
        )}

        <Box sx={{ display: "grid", gap: 1.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Seller city: <strong>{sellerCity || "Not specified"}</strong>
            {sellerGeo?.label ? ` (${sellerGeo.label})` : ""}
          </Typography>

          {geoLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Calculating distance from seller city…
              </Typography>
            </Box>
          )}

          {geoError && (
            <Alert severity="info" icon={<WarningAmberIcon />}>
              {geoError}. Distance from seller city cannot be calculated.
            </Alert>
          )}

          {distanceFromSellerKm != null && (
            <Alert severity={distanceSeverity}>
              Pin is <strong>{formatDistance(distanceFromSellerKm)}</strong> from the
              seller&apos;s listed city. Customer search only returns vehicles within{" "}
              <strong>{SEARCH_RADIUS_KM} km</strong> of the pickup coordinates they
              search from — a mismatched pin explains missing results near the seller
              city.
            </Alert>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <EventBusyIcon color="action" fontSize="small" />
          <Typography variant="subtitle1" fontWeight="bold">
            Bookings blocking search (next 6 months)
          </Typography>
        </Box>

        {bookingsLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading bookings…
            </Typography>
          </Box>
        ) : bookingsError ? (
          <Alert severity="warning">{bookingsError}</Alert>
        ) : blockingBookings.length === 0 ? (
          <Alert severity="success">
            No pending or accepted bookings overlap the next 6 months — dates are not
            blocking search.
          </Alert>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 2, overflowX: "auto" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Booking #</TableCell>
                  <TableCell>Dates</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blockingBookings.map((booking) => (
                  <TableRow key={booking.Id}>
                    <TableCell>#{booking.Id}</TableCell>
                    <TableCell>
                      {formatBookingDate(booking.StartDate)} –{" "}
                      {formatBookingDate(booking.EndDate)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={booking.type || "—"}
                        color={booking.type === "pending" ? "warning" : "info"}
                      />
                    </TableCell>
                    <TableCell>{booking.status || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default VehicleLocationInsight;
